import { useMemo } from 'react'
import { useInRouterContext, useLocation } from 'react-router-dom'
import { ApiError } from '../../lib/api'
import { newReference } from '../../lib/apiErrorText'
import { pageNameFor, stampDateTime } from '../routeError'
import { errorLead, pagePrefix } from './stateText'
import './state.css'

/* The recoverable error (state system, 2026-09-04): a read that failed
 * while the page itself is fine. One plain sentence with the status, the
 * page's name and the house date-time, a reference the officer can quote,
 * and Retry. The crash case (a page that cannot be drawn at all) is
 * RouteErrorBoundary; the two share their words and their stamp so they
 * read as one set. Never a raw API string. */

interface Props {
  /** What could not be loaded, in the officer's words: "day sheet",
   * "queue counts", "record". */
  what: string
  error: unknown
  onRetry?: () => void
  /** The page's name; defaults to the rail label of the current path. */
  page?: string
  /** compact = inside a drawer or a card. */
  compact?: boolean
  testId?: string
  className?: string
}

export default function ErrorState(props: Props) {
  const inRouter = useInRouterContext()
  return inRouter ? <Routed {...props} /> : <Plain {...props} pathname={null} />
}

function Routed(props: Props) {
  const { pathname } = useLocation()
  return <Plain {...props} pathname={pathname} />
}

function Plain({
  what,
  error,
  onRetry,
  page,
  compact,
  testId,
  className,
  pathname,
}: Props & { pathname: string | null }) {
  const api = error instanceof ApiError ? error : null
  const status = api ? api.status : null
  // A non-API failure (a thrown string, a parse error) still gets a
  // reference — minted once per error object, and logged under it.
  const { at, reference } = useMemo(() => {
    if (api) return { at: api.at, reference: api.reference }
    const minted = { at: new Date(), reference: newReference() }
    console.error(`[state-error] reference ${minted.reference}:`, error)
    return minted
  }, [api, error])
  const name = page ?? pageNameFor(pathname ?? window.location.pathname)
  const ref = `${pagePrefix(name)}-${reference}`
  return (
    <div
      className={`state-error${compact ? ' compact' : ''}${className ? ` ${className}` : ''}`}
      role="alert"
      data-testid={testId ?? 'state-error'}
      data-status={status ?? undefined}
      data-reference={ref}
    >
      <span className="state-rule" aria-hidden="true" />
      <div className="state-body">
        <p className="state-lead">{errorLead(what, status)}</p>
        <p className="state-where">
          {name} · {stampDateTime(at)}
        </p>
        <div className="state-doors">
          {onRetry ? (
            <button type="button" className="vbtn pri" onClick={onRetry} data-testid="state-retry">
              Retry
            </button>
          ) : null}
          <span className="state-ref mono" data-testid="state-reference">
            reference {ref}
          </span>
        </div>
      </div>
    </div>
  )
}
